/**
 * @module engine3d/pipes
 * @summary Turns the document's 2D connectors into real 3D pipework, and lets
 *   that pipework be re-routed by hand.
 * @description
 *   A connector in the 2D drawing is a line between two ports. In 3D it has to
 *   become something a pipefitter would recognise: it leaves each nozzle ALONG
 *   that nozzle's axis, climbs to a run elevation, crosses the plant on
 *   orthogonal legs, and turns through swept bends rather than square corners.
 *   Flanges mark both terminations and a cone carries the flow arrow.
 *
 *   Once equipment sits on different floors this does most of the work by
 *   itself — a silo discharging into an extruder throat directly below it
 *   becomes a clean vertical drop, because both ends already agree in plan.
 *
 *   Where it guesses wrong, three overrides on the connection take over, each
 *   one directly draggable in the 3D view:
 *     • `runY`       the elevation the horizontal run crosses at
 *     • `turnFirst`  which horizontal axis the run commits to first
 *     • `runOff`     how far the crossing leg is shifted from its default
 *   All three are optional, so an untouched connector routes automatically and
 *   a corrected one stays exactly where it was put.
 *
 *   // INVARIANT: routing here is independent of the 2D router. The 2D route is
 *   a schematic; the 3D route is a plausible physical one. Sharing the two
 *   produced pipes that dived through equipment to reach a schematic elbow.
 */
import type { Connection, PortKey, Shape } from '../types';
import { v3, vAdd, vMul, vNorm, type Vec3 } from './math3';
import { densify, filletPath, merge, tube, cylinder, rotateX, rotateY, translate, frustum, type Mesh } from './mesh';
import { getModelFor, depthForShape } from './models';
import { dirToWorld, placeOf, toWorld } from './placement';
import { isServiceLine } from '../engine/streams';

/** How far a run leaves a nozzle before it is allowed to turn. */
const STUB = 11;
/** Bend radius of the swept elbows. */
const BEND = 9;

/** A port resolved into world space, with the model's placement applied. */
export interface WorldPort {
  p: Vec3;
  n: Vec3;
}

/**
 * Where a connector docks on a shape, in world space.
 * Falls back to the middle of the relevant bounding-box face when the model
 * doesn't declare that port — which is what every annotation does.
 */
export function portWorld(s: Shape, key: PortKey): WorldPort {
  const model = getModelFor(s);
  const d = depthForShape(s);
  const local =
    model.ports[key] ??
    (key === 'E'
      ? { p: v3(s.w / 2, model.height * 0.5, 0), n: v3(1, 0, 0) }
      : key === 'W'
        ? { p: v3(-s.w / 2, model.height * 0.5, 0), n: v3(-1, 0, 0) }
        : key === 'N'
          ? { p: v3(0, model.height, 0), n: v3(0, 1, 0) }
          : { p: v3(0, model.height * 0.5, d / 2), n: v3(0, 0, 1) });
  return { p: toWorld(s, local.p), n: vNorm(dirToWorld(s, local.n)) };
}

/** Which horizontal axis a run commits to first, given its overrides. */
export function turnAxisOf(cn: Connection | undefined, a: WorldPort, b: WorldPort): 'x' | 'z' {
  if (cn?.turnFirst) return cn.turnFirst;
  // Follow the nozzle it just left, so a pipe never turns sideways the instant
  // it clears the flange. A vertical nozzle has no opinion, so fall back to
  // whichever way the run travels furthest.
  if (Math.abs(a.n.y) < 0.5) return Math.abs(a.n.x) > Math.abs(a.n.z) ? 'x' : 'z';
  return Math.abs(b.p.x - a.p.x) > Math.abs(b.p.z - a.p.z) ? 'x' : 'z';
}

/** The elevation a run crosses at, given its overrides. */
export function runElevationOf(cn: Connection | undefined, A: Vec3, B: Vec3): number {
  if (cn?.runY !== undefined) return cn.runY;
  // Ends at the same height stay in that plane; otherwise climb to the higher
  // of the two, which keeps pipework over head height rather than through the
  // middle of whatever stands between them.
  return Math.abs(A.y - B.y) < 5 ? (A.y + B.y) / 2 : Math.max(A.y, B.y);
}

/** Geometry of a run's centre-line, plus the pieces the drag handles need. */
export interface RouteInfo {
  path: Vec3[];
  /** Elevation of the horizontal run. */
  runY: number;
  /** Which axis the run turns along first. */
  axis: 'x' | 'z';
  /** Midpoint of the crossing leg — where the drag handle sits. */
  handle: Vec3;
  /** Default (un-offset) position of the crossing coordinate. */
  crossDefault: number;
  /** Explicit bends, when the run is shaped by hand rather than routed. */
  waypoints: Vec3[];
}

/**
 * Orthogonal legs from `from` to `to`.
 *
 * `vertFirst` continues a vertical arrival vertically before turning, which is
 * what keeps a riser looking like a riser instead of jinking sideways the
 * instant it leaves the bend below.
 */
function orthoLegs(from: Vec3, to: Vec3, vertFirst: boolean): Vec3[] {
  return vertFirst
    ? [v3(from.x, to.y, from.z), v3(to.x, to.y, from.z), to]
    : [v3(to.x, from.y, from.z), v3(to.x, from.y, to.z), to];
}

/**
 * Orthogonal legs arriving at `B` squarely along the nozzle normal `n`.
 * The last segment always runs down the nozzle axis, which is what makes the
 * flange meet the pipe face-on and the arrowhead point down the run.
 */
function legsInto(from: Vec3, B: Vec3, n: Vec3): Vec3[] {
  if (Math.abs(n.y) > 0.5) return [v3(B.x, from.y, from.z), v3(B.x, from.y, B.z), B];
  if (Math.abs(n.x) > Math.abs(n.z)) return [v3(from.x, from.y, B.z), v3(from.x, B.y, B.z), B];
  return [v3(B.x, from.y, from.z), v3(B.x, B.y, from.z), B];
}

/** Collapse coincident and collinear vertices so no elbow is built for nothing. */
function simplify3(raw: Vec3[], fallback: [Vec3, Vec3]): Vec3[] {
  const out: Vec3[] = [];
  for (const q of raw) {
    const last = out[out.length - 1];
    if (last && Math.hypot(q.x - last.x, q.y - last.y, q.z - last.z) < 0.5) continue;
    out.push(q);
  }
  for (let i = 1; i < out.length - 1; ) {
    const u = vNorm(v3(out[i].x - out[i - 1].x, out[i].y - out[i - 1].y, out[i].z - out[i - 1].z));
    const w = vNorm(v3(out[i + 1].x - out[i].x, out[i + 1].y - out[i].y, out[i + 1].z - out[i].z));
    if (u.x * w.x + u.y * w.y + u.z * w.z > 0.999) out.splice(i, 1);
    else i++;
  }
  return out.length >= 2 ? out : fallback;
}

/**
 * Route a run through explicit bends.
 *
 * Each waypoint is reached by orthogonal legs from the last, so dragging one
 * onto the same X as its neighbour produces a pure vertical riser and dragging
 * it level produces a pure horizontal run — which is how the horizontal and
 * vertical pipework gets built by hand, without a single change to the 2D
 * drawing that owns the connection.
 */
function routeWaypoints(a: WorldPort, b: WorldPort, wps: Vec3[]): RouteInfo {
  const A = vAdd(a.p, vMul(a.n, STUB));
  const B = vAdd(b.p, vMul(b.n, STUB));
  const raw: Vec3[] = [a.p, A];
  let prev = A;
  let prevDir = a.n;
  for (const wp of wps) {
    const legs = orthoLegs(prev, wp, Math.abs(prevDir.y) > 0.5);
    raw.push(...legs);
    const n = raw.length;
    prevDir = vNorm(v3(raw[n - 1].x - raw[n - 2].x, raw[n - 1].y - raw[n - 2].y, raw[n - 1].z - raw[n - 2].z));
    prev = wp;
  }
  raw.push(...legsInto(prev, B, b.n));
  raw.push(b.p);
  const path = simplify3(raw, [a.p, b.p]);
  return {
    path,
    runY: wps[0]?.y ?? A.y,
    axis: 'x',
    handle: wps[0] ?? A,
    crossDefault: 0,
    waypoints: wps,
  };
}

/**
 * The centre-line of a pipe run between two nozzles.
 *
 * Both ends get a straight stub along their own axis, then the run climbs to
 * `runY` and crosses in two orthogonal legs. With `runOff` at zero the crossing
 * coordinate coincides with the destination, so the two legs collapse into the
 * single elbow you would draw by hand; pulling the handle sideways separates
 * them into a proper offset run.
 */
export function routePipe3(a: WorldPort, b: WorldPort, cn?: Connection): RouteInfo {
  // Explicit bends replace the automatic route entirely.
  if (cn?.wp3?.length) return routeWaypoints(a, b, cn.wp3.map((q) => v3(q.x, q.y, q.z)));

  const A = vAdd(a.p, vMul(a.n, STUB));
  const B = vAdd(b.p, vMul(b.n, STUB));
  const runY = runElevationOf(cn, A, B);
  const axis = turnAxisOf(cn, a, b);

  const crossDefault = axis === 'x' ? B.x : B.z;
  const cross = crossDefault + (cn?.runOff ?? 0);

  const c1 = axis === 'x' ? v3(cross, runY, A.z) : v3(A.x, runY, cross);
  const c2 = axis === 'x' ? v3(cross, runY, B.z) : v3(B.x, runY, cross);

  const raw: Vec3[] = [
    a.p,
    A,
    v3(A.x, runY, A.z),
    c1,
    c2,
    v3(B.x, runY, B.z),
    B,
    b.p,
  ];

  const path = simplify3(raw, [a.p, b.p]);

  // The handle rides the crossing leg. When that leg has collapsed there is
  // nothing to grab, so fall back to the middle of the horizontal run.
  const legLen = Math.hypot(c2.x - c1.x, c2.z - c1.z);
  const handle =
    legLen > 6
      ? v3((c1.x + c2.x) / 2, runY, (c1.z + c2.z) / 2)
      : v3((A.x + B.x) / 2, runY, (A.z + B.z) / 2);

  return { path, runY, axis, handle, crossDefault, waypoints: [] };
}

/** Aim a mesh's +Y axis down `dir`, then move it to `at`. */
function orient(m: Mesh, at: Vec3, dir: Vec3): Mesh {
  const l = Math.hypot(dir.x, dir.y, dir.z) || 1;
  rotateX(m, Math.acos(Math.max(-1, Math.min(1, dir.y / l))));
  rotateY(m, Math.atan2(dir.x, dir.z));
  return translate(m, at.x, at.y, at.z);
}

export interface PipeGeometry {
  /** The pipe itself. */
  body: Mesh;
  /** Flanges, arrowheads and other fittings — drawn in a contrasting material. */
  fittings: Mesh;
  route: RouteInfo;
  radius: number;
  /**
   * The FILLETED centre-line the body was actually swept along, start to end.
   *
   * `route.path` is the Manhattan route with square corners; this is what came
   * out of it once the elbows were rounded. The flow animation measures
   * distance along the run on this, and on the route it would stall at every
   * bend — a corner cut of two bend radii of path collapses into an arc barely
   * three quarters as long.
   */
  centre: Vec3[];
  /** Distance along the run at each vertex of `body`, in world units. */
  arc: number[];
}

/**
 * Build the 3D geometry for one connector.
 * Dashed line styles become slim lagged service lines rather than process pipe,
 * which preserves the distinction the 2D drawing makes with its dash patterns.
 */
/**
 * @param flowStep  Longest allowed section of pipe wall, in world units, or 0
 *   for the default sweep. Only the flow animation asks for this: a shaded
 *   triangle is one flat colour, so a straight leg swept as a single quad strip
 *   cannot show anything travelling down it. Off by default because it is a
 *   real cost — several times the triangles on every run in the plant — and one
 *   worth paying only while somebody is watching the flow.
 */
export function buildPipe(
  cn: Connection,
  a: WorldPort,
  b: WorldPort,
  flowStep = 0,
): PipeGeometry {
  const isService = isServiceLine(cn);
  const lw = cn.lineWidth || 1.5;
  const r = isService ? Math.max(1.1, lw * 0.75) : Math.max(2.1, 1.5 + lw * 0.55);
  const route = routePipe3(a, b, cn);
  const seg = isService ? 8 : 12;
  // Elbow radius is a property of the run, chosen per pipe exactly as it would
  // be specified on a real line.
  const factor = cn.bend3 === 'short' ? 1.7 : cn.bend3 === 'mitre' ? 0.18 : 3.4;
  const bendR = Math.min(BEND, r * factor);
  const centre = filletPath(route.path, bendR, cn.bend3 === 'mitre' ? 2 : 5);
  const arc: number[] = [];
  const body = tube(densify(centre, flowStep), r, seg, true, arc);

  const fittings: Mesh[] = [];
  // A flange at each termination, sitting square on the nozzle face.
  for (const [end, dir] of [
    [a.p, a.n],
    [b.p, b.n],
  ] as [Vec3, Vec3][]) {
    fittings.push(orient(cylinder(r * 1.85, 1.8, { seg }), vAdd(end, vMul(dir, 0.6)), dir));
  }
  // Flow arrow: a cone on the last leg, pulled back clear of the flange.
  const path = route.path;
  if (cn.arrowHead !== 'none') {
    const n = path.length;
    const dir = vNorm(v3(path[n - 1].x - path[n - 2].x, path[n - 1].y - path[n - 2].y, path[n - 1].z - path[n - 2].z));
    fittings.push(orient(frustum(r * 2, 0, r * 3.4, { seg }), vAdd(path[n - 1], vMul(dir, -(r * 5))), dir));
    if (cn.arrowHead === 'both') {
      const d2 = vNorm(v3(path[0].x - path[1].x, path[0].y - path[1].y, path[0].z - path[1].z));
      fittings.push(orient(frustum(r * 2, 0, r * 3.4, { seg }), vAdd(path[0], vMul(d2, -(r * 5))), d2));
    }
  }
  return { body, fittings: merge(...fittings), route, radius: r, centre, arc };
}

/** The point on a run where its label should float — middle of the longest leg. */
export function pipeLabelAnchor(path: Vec3[]): Vec3 {
  let bi = 0;
  let bd = -1;
  for (let i = 0; i < path.length - 1; i++) {
    const d = Math.hypot(path[i + 1].x - path[i].x, path[i + 1].y - path[i].y, path[i + 1].z - path[i].z);
    if (d > bd) {
      bd = d;
      bi = i;
    }
  }
  return v3(
    (path[bi].x + path[bi + 1].x) / 2,
    (path[bi].y + path[bi + 1].y) / 2,
    (path[bi].z + path[bi + 1].z) / 2,
  );
}

// ─── Re-routing by hand ──────────────────────────────────────────────────

/** Resolve both ends of a connection, or null when a shape has been deleted. */
export function endsOf(cn: Connection, byId: Map<string, Shape>): [WorldPort, WorldPort] | null {
  const f = byId.get(cn.fromId);
  const t = byId.get(cn.toId);
  if (!f || !t) return null;
  return [portWorld(f, cn.fromPort || 'E'), portWorld(t, cn.toPort || 'W')];
}

/** Apply a dragged handle position back onto the connection's overrides. */
export function applyHandle(cn: Connection, a: WorldPort, b: WorldPort, to: Vec3): void {
  const info = routePipe3(a, b, cn);
  cn.runY = Math.round(to.y * 10) / 10;
  const moved = info.axis === 'x' ? to.x : to.z;
  const off = moved - info.crossDefault;
  if (Math.abs(off) < 1.5) delete cn.runOff;
  else cn.runOff = Math.round(off * 10) / 10;
}

/** Flip which horizontal axis the run commits to first. */
export function flipTurnAxis(cn: Connection, a: WorldPort, b: WorldPort): void {
  const cur = turnAxisOf(cn, a, b);
  cn.turnFirst = cur === 'x' ? 'z' : 'x';
  // The offset was measured against the other axis and is meaningless now.
  delete cn.runOff;
}

/** Drop every 3D routing override so the run goes back to automatic. */
export function resetRoute3(cn: Connection): void {
  delete cn.runY;
  delete cn.turnFirst;
  delete cn.runOff;
  delete cn.wp3;
}

/** True when this run has been re-routed by hand. */
export function isRerouted(cn: Connection): boolean {
  return (
    cn.runY !== undefined ||
    cn.turnFirst !== undefined ||
    cn.runOff !== undefined ||
    (cn.wp3?.length ?? 0) > 0
  );
}

/** How many explicit bends this run carries. */
export function bendCount(cn: Connection): number {
  return cn.wp3?.length ?? 0;
}

/**
 * Add a bend to a run.
 *
 * The first one is seeded at the route's current crossing point, so the pipe
 * does not jump the moment it becomes hand-shaped — it simply gains a handle
 * where the corner already was. Later ones subdivide the LONGEST gap between
 * existing anchors, which is where there is room to put one.
 */
export function addBend(cn: Connection, a: WorldPort, b: WorldPort): number {
  const info = routePipe3(a, b, cn);
  const wps = (cn.wp3 ?? []).map((q) => v3(q.x, q.y, q.z));
  let index: number;
  if (!wps.length) {
    wps.push(v3(info.handle.x, info.handle.y, info.handle.z));
    index = 0;
  } else {
    const A = vAdd(a.p, vMul(a.n, STUB));
    const B = vAdd(b.p, vMul(b.n, STUB));
    const anchors = [A, ...wps, B];
    let best = 0;
    let bestLen = -1;
    for (let i = 0; i < anchors.length - 1; i++) {
      const len = Math.hypot(
        anchors[i + 1].x - anchors[i].x,
        anchors[i + 1].y - anchors[i].y,
        anchors[i + 1].z - anchors[i].z,
      );
      if (len > bestLen) {
        bestLen = len;
        best = i;
      }
    }
    const mid = v3(
      (anchors[best].x + anchors[best + 1].x) / 2,
      (anchors[best].y + anchors[best + 1].y) / 2,
      (anchors[best].z + anchors[best + 1].z) / 2,
    );
    index = best;
    wps.splice(best, 0, mid);
  }
  cn.wp3 = wps.map((q) => ({ x: round1(q.x), y: round1(q.y), z: round1(q.z) }));
  // Explicit bends supersede the automatic crossing overrides.
  delete cn.runY;
  delete cn.runOff;
  delete cn.turnFirst;
  return index;
}

/** Remove one bend. Dropping the last returns the run to automatic routing. */
export function removeBend(cn: Connection, index: number): void {
  if (!cn.wp3 || index < 0 || index >= cn.wp3.length) return;
  cn.wp3.splice(index, 1);
  if (!cn.wp3.length) delete cn.wp3;
}

/** Move one bend to a new position. */
export function moveBend(cn: Connection, index: number, to: Vec3): void {
  if (!cn.wp3 || index < 0 || index >= cn.wp3.length) return;
  cn.wp3[index] = { x: round1(to.x), y: round1(to.y), z: round1(to.z) };
}

const round1 = (v: number): number => Math.round(v * 10) / 10;

/** Placement is re-exported so callers don't need a second import for it. */
export { placeOf };
